import { useCallback, useEffect, useRef, useState } from 'react';
import type { AoMeta, Graphic } from '../sim/types';
import { MapView, type MapReady } from '../map/MapView';
import { Overlay, type OverlayUnit } from '../render/Overlay';
import { isValidSidc } from '../render/symbols';

/**
 * Temporary proof screen for checkpoint 1: a MapLibre basemap with hillshade, a PixiJS overlay
 * with MIL-STD-2525 (the US military symbology standard) unit symbols moving across the AO
 * (area of operations), two control graphics and one enemy contact. Replaced in Task 12 by the
 * real stages driven by the simulation.
 */

const SIDC_BLUE_ARMOR = 'SFGPUCA---D----'; // friendly armor platoon
const SIDC_RED_MECH = 'SHGPUCIZ--D----'; // hostile mechanized infantry platoon

const M = 100; // centimeters per meter
const SPEED_CM_PER_SEC = 250 * M; // 1 km per 4 s

/** Used when the built AO is missing so the proof still runs. */
function syntheticMeta(): AoMeta {
  const lat = 35.31;
  const lon = -116.62;
  const mPerDegLat = 111320;
  const mPerDegLon = 111320 * Math.cos((lat * Math.PI) / 180);
  return {
    name: 'synthetic',
    title: 'Synthetic AO',
    lat,
    lon,
    sizeM: 15000,
    cellM: 50,
    w: 300,
    h: 300,
    originLat: lat - 7500 / mPerDegLat,
    originLon: lon - 7500 / mPerDegLon,
    mPerDegLat,
    mPerDegLon,
  };
}

interface Mover extends OverlayUnit {
  vx: number;
}

function makeUnits(sizeM: number): Mover[] {
  const units: Mover[] = [];
  for (let i = 0; i < 3; i++) {
    units.push({
      id: i + 1,
      x: 600 * M,
      y: (4000 + i * 3000) * M,
      sidc: SIDC_BLUE_ARMOR,
      side: 0,
      strength: 100 - i * 18,
      suppression: i === 2 ? 55 : 0,
      selected: i === 0,
      pending: i === 1,
      label: `A${i + 1}`,
      vx: SPEED_CM_PER_SEC,
    });
  }
  for (let i = 0; i < 3; i++) {
    units.push({
      id: 10 + i,
      x: 2200 * M,
      y: (5500 + i * 3000) * M,
      sidc: SIDC_RED_MECH,
      side: 1,
      strength: 90 - i * 25,
      suppression: i === 1 ? 35 : 0,
      label: `R${i + 1}`,
      vx: SPEED_CM_PER_SEC * 0.8,
    });
  }
  return units;
}

function makeGraphics(sizeM: number): Graphic[] {
  const s = sizeM * M;
  return [
    {
      id: 'pl-red',
      kind: 'pl',
      name: 'PL RED',
      pts: [
        { x: s * 0.5, y: s * 0.08 },
        { x: s * 0.52, y: s * 0.92 },
      ],
    },
    {
      id: 'obj-cobalt',
      kind: 'obj',
      name: 'OBJ COBALT',
      pts: [
        { x: s * 0.78, y: s * 0.44 },
        { x: s * 0.92, y: s * 0.46 },
        { x: s * 0.93, y: s * 0.62 },
        { x: s * 0.79, y: s * 0.6 },
      ],
    },
  ];
}

export function MapProof() {
  const [meta, setMeta] = useState<AoMeta | null>(null);
  const [aoLabel, setAoLabel] = useState('loading area of operations…');
  const overlayRef = useRef<Overlay | null>(null);
  const unitsRef = useRef<Mover[]>([]);
  const [picked, setPicked] = useState<string>('—');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/ao/ntc/meta.json');
        if (!res.ok) throw new Error(String(res.status));
        const json = (await res.json()) as AoMeta;
        if (cancelled) return;
        setMeta(json);
        setAoLabel(json.title);
      } catch {
        if (cancelled) return;
        const m = syntheticMeta();
        setMeta(m);
        setAoLabel(`${m.title} (synthetic fallback)`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleReady = useCallback(
    ({ map, container, projection }: MapReady) => {
      const sizeM = projection.widthM;
      for (const sidc of [SIDC_BLUE_ARMOR, SIDC_RED_MECH]) {
        if (!isValidSidc(sidc)) console.error(`[proof] invalid SIDC ${sidc}`);
      }

      const overlay = new Overlay(container, map, projection);
      overlayRef.current = overlay;
      unitsRef.current = makeUnits(sizeM);

      overlay.setGraphics(makeGraphics(sizeM));
      overlay.setContacts([
        {
          enemyId: 99,
          x: sizeM * M * 0.62,
          y: sizeM * M * 0.55,
          errorM: 450,
          ageMin: 3,
          label: 'VEH',
        },
      ]);
      overlay.setUnits(unitsRef.current);
      overlay.onClickUnit((id) => setPicked(`unit ${id}`));
      overlay.onClickMap((cm) => setPicked(`map ${(cm.x / M / 1000).toFixed(2)} km E, ${(cm.y / M / 1000).toFixed(2)} km N`));

      // Straight-line march east; no flow field until Task 4 lands.
      let last = performance.now();
      let raf = requestAnimationFrame(function step(now: number) {
        const dt = Math.min(0.1, (now - last) / 1000);
        last = now;
        const limit = sizeM * M;
        for (const u of unitsRef.current) {
          u.x += u.vx * dt;
          if (u.x > limit) u.x = 0;
        }
        overlay.setUnits(unitsRef.current);
        raf = requestAnimationFrame(step);
      });

      // Handles for the browser verification pass.
      (window as unknown as Record<string, unknown>).__proof = { map, overlay, projection, units: unitsRef };

      return () => {
        cancelAnimationFrame(raf);
        overlay.destroy();
        overlayRef.current = null;
        delete (window as unknown as Record<string, unknown>).__proof;
      };
    },
    [],
  );

  return (
    <div className="relative h-full w-full bg-ink">
      {meta && <MapView meta={meta} onReady={handleReady} />}
      <div className="pointer-events-none absolute left-4 top-4 z-10 rounded border border-slate-700 bg-slate-900/85 px-3 py-2">
        <div className="mono text-[10px] tracking-[0.3em] text-fog">WINDTUNNEL · CP1</div>
        <div className="mono mt-1 text-xs text-paper">{aoLabel}</div>
        <div className="mono mt-1 text-[11px] text-fog">click: {picked}</div>
      </div>
    </div>
  );
}
